import React from 'react'
import { datalayerHandler } from '../../../../utils/datalayer'
import { Image, ImageOptions } from '../../../../utils/image'
import { Link } from '../../../../utils/link'
import { linkGridBlockClasses } from '../../../../utils/style'
import { convertReactNodeToString, getClassName, htmlDecode, unEntity } from '../../../../utils/utils'
import { useInView } from '../../../hooks/useInView'
import { LinkGridBlock, LinkGridItem, SVGIconOptions } from '../blocksState'

export type LinkGridBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  headerClassName: string
  titleClassName: string
  introClassName: string
  contentClassName: string
  listClassName: string
  gridItemClassName: string
  gridArticleClassName: string
  gridWithLinkClassName: string
  gridWithoutLinkClassName: string
  gridHeaderClassName: string
  gridContentClassName: string
  gridTitleClassName: string
  gridLabelClassName: string
  gridIntroClassName: string
  gridFauxLinkWrapperClassName: string
  gridFauxLinkClassName: string
  gridFauxLinkLabelClassName: string
  gridFauxLinkIconClassName: string
  gridVisualClassName: string
  gridImageClassName: string
  gridPictureClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickableClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  iconId: string
  iconFauxLinkId: string
}>

export const LinkGridBlockLayout = (props: LinkGridBlock) => {
  const { inview, index, items, intro, primaryAction, title, withImages } = props
  const classes = linkGridBlockClasses(withImages)

  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("LinkGridBlockLayout inView:", inview),
    !inview?.triggerOnce
  )

  return (
    <section
      key={`block-${index}`}
      id={`io-${index}`}
      ref={ref}
      className={getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')}
    >
      <div className={getClassName(classes, 'innerClassName', 'py-5')}>
        {(title || intro) && (
          <header className={getClassName(classes, 'headerClassName', 'text-center')}>
            {title && (
              <h2 className={getClassName(classes, 'titleClassName', 'mb-3')}>
                <span id={`${index}`} aria-hidden="true" className="block__anchor" />
                {title}
              </h2>
            )}
            {intro && <p className={getClassName(classes, 'introClassName', '')}>{intro}</p>}
          </header>
        )}

        {items && items.length > 0 && (
          <div className={getClassName(classes, 'contentClassName', '')}>
            <ul className={getClassName(classes, 'listClassName', 'row list-unstyled mb-0')}>
              {items.map((item, i) => (
                <li
                  key={`grid-${index}-${i}`}
                  className={getClassName(classes, 'gridItemClassName', 'col-md-6 col-lg-4 py-3')}
                >
                  <article className={getClassName(classes, 'gridArticleClassName', '')}>
                    {item.link.url ? (
                      <Link
                        url={item.link.url}
                        title={item.link.title}
                        className={getClassName(
                          classes,
                          'gridWithLinkClassName',
                          'd-flex flex-column-reverse text-secondary'
                        )}
                      >
                        <LinkGridItemLayout
                          {...item}
                          imageOptions={props.withImages ? props.imageOptions : undefined}
                          classes={classes}
                        />
                      </Link>
                    ) : (
                      <div className={getClassName(classes, 'gridWithoutLinkClassName', 'd-flex flex-column-reverse')}>
                        <LinkGridItemLayout
                          {...item}
                          imageOptions={props.withImages ? props.imageOptions : undefined}
                          classes={classes}
                        />
                      </div>
                    )}
                  </article>
                </li>
              ))}
            </ul>
          </div>
        )}

        {primaryAction && primaryAction.title && (
          <footer className={getClassName(classes, 'footerClassName', 'text-center')}>
            <div className={getClassName(classes, 'clickableWrapperClassName', 'd-inline-block')}>
              <Link
                url={primaryAction.url}
                title={primaryAction.title}
                className={getClassName(classes, 'clickableClassName', 'btn btn-dark d-inline-flex')}
                additionalOnClick={() =>
                  datalayerHandler('link-grid', undefined, undefined, {
                    kind: 'link',
                    title: convertReactNodeToString(title),
                    link: primaryAction.url,
                    label: primaryAction.title,
                  })
                }
              >
                <span className={getClassName(classes, 'clickableLabelClassName', '')}>
                  {htmlDecode(primaryAction.title)}
                </span>
                {primaryAction.SVGIcon && (
                  <svg
                    className={getClassName(classes, 'clickableIconClassName', 'ml-3')}
                    aria-hidden="true"
                    width={primaryAction.SVGIcon.width ? primaryAction.SVGIcon.width : '24px'}
                    height={primaryAction.SVGIcon.height ? primaryAction.SVGIcon.height : '24px'}
                  >
                    <use xlinkHref={getClassName(classes, 'iconId', '#icon--chevron-right-large')} />
                  </svg>
                )}
              </Link>
            </div>
          </footer>
        )}
      </div>
    </section>
  )
}

export const LinkGridItemLayout = (
  props: LinkGridItem & {
    itemsSVGIcon?: SVGIconOptions
    imageOptions?: ImageOptions
    classes: LinkGridBlockStyle
  }
) => {
  const { intro, title, image, label, link, itemsSVGIcon, imageOptions, classes } = props
  return (
    <>
      {(title || label || intro || (link && link.title)) && (
        <div className={getClassName(classes, 'gridContentClassName', 'card-body px-0 pb-0')}>
          {(title || label) && (
            <header className={getClassName(classes, 'gridHeaderClassName', '')}>
              {label && (
                <span className={getClassName(classes, 'gridLabelClassName', 'mb-2')}>{htmlDecode(label)}</span>
              )}
              {title && <h3 className={getClassName(classes, 'gridTitleClassName', 'mb-2')}>{htmlDecode(title)}</h3>}
            </header>
          )}

          {intro && <p className={getClassName(classes, 'gridIntroClassName', '')}>{unEntity(intro)}</p>}

          {link && link.title && (
            <footer
              className={getClassName(classes, 'gridFauxLinkWrapperClassName', 'd-inline-flex align-items-center')}
            >
              <div className={getClassName(classes, 'gridFauxLinkClassName', 'btn btn-link px-0')}>
                <span className={getClassName(classes, 'gridFauxLinkLabelClassName', '')}>
                  {htmlDecode(link.title)}
                </span>
                {itemsSVGIcon && (
                  <svg
                    className={getClassName(classes, 'gridFauxLinkIconClassName', 'ml-3')}
                    aria-hidden="true"
                    width={itemsSVGIcon.width ? itemsSVGIcon.width : '24px'}
                    height={itemsSVGIcon.height ? itemsSVGIcon.height : '24px'}
                  >
                    <use xlinkHref={getClassName(classes, 'iconFauxLinkId', '#icon--chevron-right-large')} />
                  </svg>
                )}
              </div>
            </footer>
          )}
        </div>
      )}

      {image && imageOptions && (
        <div className={getClassName(classes, 'gridVisualClassName', 'card-img-top')}>
          <Image
            {...imageOptions}
            {...image}
            classNames={{
              image: getClassName(classes, 'gridImageClassName', 'w-100'),
              picture: getClassName(classes, 'gridPictureClassName', ''),
            }}
          />
        </div>
      )}
    </>
  )
}
